/**
 * Relationship Compass - Relationship Patterns & Dynamics Assessment
 *
 * This assessment identifies recurring patterns, roles, and interaction cycles in the relationship.
 * It explores how couples navigate autonomy vs. togetherness, decision-making, emotional support,
 * responsibilities, growth, and trust dynamics.
 *
 * Design Philosophy:
 * - Reveals systemic patterns, not just individual behaviours
 * - Celebrates strengths whilst identifying growth opportunities
 * - Connects patterns to attachment styles and family-of-origin dynamics
 * - Non-judgmental—all patterns developed for good reasons
 * - DDD brand voice: warm, research-grounded, insightful, authentic
 * - Australian English spelling throughout
 */

const RelationshipPatternsProfile = {
  id: 'RPP-1',
  title: "Relationship Patterns & Dynamics",
  subtitle: "Identify the recurring patterns and cycles that shape your relationship",
  duration: "20-25 minutes",
  questionCount: 24, // 4 questions per pattern category

  introduction: {
    heading: "Understanding Your Relationship Patterns",
    content: `Every relationship develops patterns: recurring ways of interacting that become invisible to us over time. Some patterns strengthen connection. Others erode it.

This assessment helps you see the patterns you might not even realise you're living in: how you balance autonomy and togetherness, who makes decisions, how you support each other emotionally, who carries what responsibilities, how you navigate growth, and how trust operates between you.

**How it works:**
- You'll answer questions about how you typically navigate key relationship dynamics
- Focus on actual patterns, not aspirational ideals
- Your partner can complete this separately to compare perspectives
- Results will reveal both strengths and opportunities for change

These patterns aren't permanent. Awareness is the first step toward intentional change.

Ready to see your patterns clearly?`,
  },

  patternCategories: {
    autonomyTogetherness: {
      name: "Autonomy vs. Togetherness",
      description: "How you balance independence, personal space, and individual identity with connection and shared life",
      icon: "⚖️",
      color: "purple",
      module: 1, // Links to Module 1: Understanding Your Patterns
      dimensions: ['independenceBalance', 'spaceNeeds', 'identityMaintenance'],
      questions: [0, 1, 2, 3]
    },

    decisionMaking: {
      name: "Decision-Making Patterns",
      description: "How power is distributed, who influences major choices, and how you make decisions together",
      icon: "🎯",
      color: "blue",
      module: 1, // Links to Module 1: Understanding Your Patterns
      dimensions: ['powerDistribution', 'influenceTactics', 'collaborationLevel'],
      questions: [4, 5, 6, 7]
    },

    emotionalSupport: {
      name: "Emotional Support Patterns",
      description: "How you respond to each other's distress, offer comfort, and provide empathy",
      icon: "💙",
      color: "teal",
      module: 2, // Links to Module 2: Recognition & Appreciation
      dimensions: ['responsivenessToDistress', 'comfortGiving', 'empathyLevel'],
      questions: [8, 9, 10, 11]
    },

    responsibilityDistribution: {
      name: "Responsibility Distribution",
      description: "How tasks, labour, and mental load are divided and whether the division feels fair",
      icon: "⚙️",
      color: "amber",
      module: 1, // Links to Module 1: Understanding Your Patterns
      dimensions: ['taskDivision', 'mentalLoadAwareness', 'fairnessPerception'],
      questions: [12, 13, 14, 15]
    },

    growthChange: {
      name: "Growth & Change Patterns",
      description: "How you support each other's personal development, adapt to change, and evolve together",
      icon: "🌱",
      color: "green",
      module: 7, // Links to Module 7: Growing Together
      dimensions: ['developmentSupport', 'changeAdaptability', 'evolutionCompatibility'],
      questions: [16, 17, 18, 19]
    },

    trustTransparency: {
      name: "Trust & Transparency",
      description: "How openly you share, what privacy boundaries exist, and how honesty operates",
      icon: "🔐",
      color: "indigo",
      module: 6, // Links to Module 6: Trust & Repair
      dimensions: ['opennessLevel', 'privacyBoundaries', 'honestyPractices'],
      questions: [20, 21, 22, 23]
    }
  },

  questions: [
    // AUTONOMY VS. TOGETHERNESS (Questions 0-3)
    {
      id: 0,
      category: 'autonomyTogetherness',
      type: "scenario",
      text: "Your partner wants to spend the weekend with their friends. Your typical response:",
      options: [
        {
          text: "Encourage them and enjoy my own time; independence is healthy",
          weights: {
            independenceBalance: 2,
            spaceNeeds: 2,
            identityMaintenance: 2
          }
        },
        {
          text: "Feel a bit anxious but don't express it. I worry when we're apart",
          weights: {
            independenceBalance: -2,
            spaceNeeds: -1,
            identityMaintenance: -1
          }
        },
        {
          text: "Suggest we all hang out together; I prefer shared experiences",
          weights: {
            independenceBalance: -1,
            spaceNeeds: -2,
            identityMaintenance: 0
          }
        },
        {
          text: "Feel relieved. I value our separate lives and interests",
          weights: {
            independenceBalance: 2,
            spaceNeeds: 2,
            identityMaintenance: 2
          }
        }
      ]
    },

    {
      id: 1,
      category: 'autonomyTogetherness',
      type: "scenario",
      text: "When thinking about your individual hobbies and interests:",
      options: [
        {
          text: "We each have our own separate interests and that strengthens our relationship",
          weights: {
            independenceBalance: 2,
            spaceNeeds: 2,
            identityMaintenance: 2
          }
        },
        {
          text: "I've given up some interests to spend more time together",
          weights: {
            independenceBalance: -1,
            spaceNeeds: -2,
            identityMaintenance: -2
          }
        },
        {
          text: "We mostly share the same interests; separate hobbies feel like growing apart",
          weights: {
            independenceBalance: -2,
            spaceNeeds: -2,
            identityMaintenance: -1
          }
        },
        {
          text: "We balance individual pursuits with shared activities",
          weights: {
            independenceBalance: 1,
            spaceNeeds: 1,
            identityMaintenance: 1
          }
        }
      ]
    },

    {
      id: 2,
      category: 'autonomyTogetherness',
      type: "scenario",
      text: "Your sense of individual identity within the relationship:",
      options: [
        {
          text: "I have a strong sense of who I am separate from the relationship",
          weights: {
            independenceBalance: 2,
            spaceNeeds: 1,
            identityMaintenance: 2
          }
        },
        {
          text: "I sometimes lose myself in 'we' and forget who 'I' am",
          weights: {
            independenceBalance: -2,
            spaceNeeds: -2,
            identityMaintenance: -2
          }
        },
        {
          text: "My identity is deeply intertwined with the relationship. That's how I like it",
          weights: {
            independenceBalance: -1,
            spaceNeeds: -1,
            identityMaintenance: -1
          }
        },
        {
          text: "I'm working on balancing 'me,' 'you,' and 'us'",
          weights: {
            independenceBalance: 1,
            spaceNeeds: 0,
            identityMaintenance: 1
          }
        }
      ]
    },

    {
      id: 3,
      category: 'autonomyTogetherness',
      type: "scenario",
      text: "When you need personal space or alone time:",
      options: [
        {
          text: "I ask for it directly and my partner understands",
          weights: {
            independenceBalance: 2,
            spaceNeeds: 2,
            identityMaintenance: 2
          }
        },
        {
          text: "I feel guilty asking for space, like I'm rejecting my partner",
          weights: {
            independenceBalance: -2,
            spaceNeeds: -2,
            identityMaintenance: -1
          }
        },
        {
          text: "I rarely need space; being together energises me",
          weights: {
            independenceBalance: -1,
            spaceNeeds: -2,
            identityMaintenance: 0
          }
        },
        {
          text: "I take it without explaining, which sometimes causes misunderstanding",
          weights: {
            independenceBalance: 1,
            spaceNeeds: 2,
            identityMaintenance: 0
          }
        }
      ]
    },

    // DECISION-MAKING PATTERNS (Questions 4-7)
    {
      id: 4,
      category: 'decisionMaking',
      type: "scenario",
      text: "When making major decisions (moving, career changes, big purchases):",
      options: [
        {
          text: "We discuss together and reach consensus before deciding",
          weights: {
            powerDistribution: 2,
            influenceTactics: 1,
            collaborationLevel: 2
          }
        },
        {
          text: "One of us usually takes the lead and the other goes along",
          weights: {
            powerDistribution: -2,
            influenceTactics: 0,
            collaborationLevel: -1
          }
        },
        {
          text: "We each make decisions in our own domains independently",
          weights: {
            powerDistribution: 0,
            influenceTactics: -1,
            collaborationLevel: 0
          }
        },
        {
          text: "Decisions get made but the process often feels unequal",
          weights: {
            powerDistribution: -2,
            influenceTactics: -1,
            collaborationLevel: -2
          }
        }
      ]
    },

    {
      id: 5,
      category: 'decisionMaking',
      type: "scenario",
      text: "Who typically has more influence in your relationship?",
      options: [
        {
          text: "Influence is balanced: we each have equal say",
          weights: {
            powerDistribution: 2,
            influenceTactics: 1,
            collaborationLevel: 2
          }
        },
        {
          text: "My partner has more influence: their preferences usually win",
          weights: {
            powerDistribution: -2,
            influenceTactics: -1,
            collaborationLevel: 0
          }
        },
        {
          text: "I have more influence; I'm more assertive or decisive",
          weights: {
            powerDistribution: -2,
            influenceTactics: 1,
            collaborationLevel: -1
          }
        },
        {
          text: "Influence varies by domain: we each lead in different areas",
          weights: {
            powerDistribution: 1,
            influenceTactics: 0,
            collaborationLevel: 1
          }
        }
      ]
    },

    {
      id: 6,
      category: 'decisionMaking',
      type: "scenario",
      text: "When you disagree on a decision:",
      options: [
        {
          text: "We keep discussing until we find a solution that works for both of us",
          weights: {
            powerDistribution: 2,
            influenceTactics: 1,
            collaborationLevel: 2
          }
        },
        {
          text: "One of us usually gives in to keep the peace",
          weights: {
            powerDistribution: -1,
            influenceTactics: -1,
            collaborationLevel: -1
          }
        },
        {
          text: "We compromise, each giving up something to reach agreement",
          weights: {
            powerDistribution: 1,
            influenceTactics: 0,
            collaborationLevel: 1
          }
        },
        {
          text: "Disagreements can drag on unresolved for a long time",
          weights: {
            powerDistribution: 0,
            influenceTactics: -2,
            collaborationLevel: -2
          }
        }
      ]
    },

    {
      id: 7,
      category: 'decisionMaking',
      type: "scenario",
      text: "How decisions actually get made in your relationship:",
      options: [
        {
          text: "Through transparent discussion where both voices matter equally",
          weights: {
            powerDistribution: 2,
            influenceTactics: 2,
            collaborationLevel: 2
          }
        },
        {
          text: "The louder or more persistent voice usually wins",
          weights: {
            powerDistribution: -2,
            influenceTactics: -2,
            collaborationLevel: -2
          }
        },
        {
          text: "We avoid making decisions together. It's too conflict-prone",
          weights: {
            powerDistribution: -1,
            influenceTactics: -1,
            collaborationLevel: -2
          }
        },
        {
          text: "Sometimes collaborative, sometimes one-sided. It's inconsistent",
          weights: {
            powerDistribution: 0,
            influenceTactics: 0,
            collaborationLevel: 0
          }
        }
      ]
    },

    // EMOTIONAL SUPPORT PATTERNS (Questions 8-11)
    {
      id: 8,
      category: 'emotionalSupport',
      type: "scenario",
      text: "When you're having a really hard day:",
      options: [
        {
          text: "My partner notices without me saying and offers comfort",
          weights: {
            responsivenessToDistress: 2,
            comfortGiving: 2,
            empathyLevel: 2
          }
        },
        {
          text: "I need to explicitly ask for support; it's not offered automatically",
          weights: {
            responsivenessToDistress: -1,
            comfortGiving: 0,
            empathyLevel: -1
          }
        },
        {
          text: "My partner tries to fix the problem rather than offering emotional comfort",
          weights: {
            responsivenessToDistress: 0,
            comfortGiving: -2,
            empathyLevel: -1
          }
        },
        {
          text: "I usually don't share when I'm struggling. I handle it alone",
          weights: {
            responsivenessToDistress: -2,
            comfortGiving: -1,
            empathyLevel: 0
          }
        }
      ]
    },

    {
      id: 9,
      category: 'emotionalSupport',
      type: "scenario",
      text: "When your partner is upset or distressed:",
      options: [
        {
          text: "I offer empathy and presence: 'I'm here, tell me what you need'",
          weights: {
            responsivenessToDistress: 2,
            comfortGiving: 2,
            empathyLevel: 2
          }
        },
        {
          text: "I jump into problem-solving mode: 'Let's fix this'",
          weights: {
            responsivenessToDistress: 1,
            comfortGiving: -1,
            empathyLevel: 0
          }
        },
        {
          text: "I feel uncomfortable and don't know what to do",
          weights: {
            responsivenessToDistress: -2,
            comfortGiving: -2,
            empathyLevel: -1
          }
        },
        {
          text: "I offer reassurance: 'It'll be okay,' 'Don't worry about it'",
          weights: {
            responsivenessToDistress: 0,
            comfortGiving: -1,
            empathyLevel: -1
          }
        }
      ]
    },

    {
      id: 10,
      category: 'emotionalSupport',
      type: "scenario",
      text: "The emotional support in your relationship:",
      options: [
        {
          text: "Is mutual: we're both there for each other consistently",
          weights: {
            responsivenessToDistress: 2,
            comfortGiving: 2,
            empathyLevel: 2
          }
        },
        {
          text: "Is one-sided: one of us does most of the emotional labour",
          weights: {
            responsivenessToDistress: -2,
            comfortGiving: -2,
            empathyLevel: -1
          }
        },
        {
          text: "Exists but feels transactional: 'I supported you, now you support me'",
          weights: {
            responsivenessToDistress: 0,
            comfortGiving: -1,
            empathyLevel: -1
          }
        },
        {
          text: "Is inconsistent: sometimes great, sometimes absent",
          weights: {
            responsivenessToDistress: 0,
            comfortGiving: 0,
            empathyLevel: 0
          }
        }
      ]
    },

    {
      id: 11,
      category: 'emotionalSupport',
      type: "scenario",
      text: "When you share something vulnerable:",
      options: [
        {
          text: "My partner responds with empathy and makes me feel understood",
          weights: {
            responsivenessToDistress: 2,
            comfortGiving: 2,
            empathyLevel: 2
          }
        },
        {
          text: "They minimise it: 'It's not that bad' or 'You're overreacting'",
          weights: {
            responsivenessToDistress: -2,
            comfortGiving: -2,
            empathyLevel: -2
          }
        },
        {
          text: "They listen but don't seem to truly understand what I'm feeling",
          weights: {
            responsivenessToDistress: 0,
            comfortGiving: 0,
            empathyLevel: -1
          }
        },
        {
          text: "I rarely share vulnerably. It doesn't feel safe enough",
          weights: {
            responsivenessToDistress: -2,
            comfortGiving: -1,
            empathyLevel: -2
          }
        }
      ]
    },

    // RESPONSIBILITY DISTRIBUTION (Questions 12-15)
    {
      id: 12,
      category: 'responsibilityDistribution',
      type: "scenario",
      text: "The division of household tasks and responsibilities:",
      options: [
        {
          text: "Is fair and balanced: we each contribute equally",
          weights: {
            taskDivision: 2,
            mentalLoadAwareness: 2,
            fairnessPerception: 2
          }
        },
        {
          text: "Is unequal but feels okay; we each contribute differently",
          weights: {
            taskDivision: 0,
            mentalLoadAwareness: 1,
            fairnessPerception: 1
          }
        },
        {
          text: "Is unequal and a source of resentment for me",
          weights: {
            taskDivision: -2,
            mentalLoadAwareness: -1,
            fairnessPerception: -2
          }
        },
        {
          text: "We haven't really discussed it. Tasks just fall where they fall",
          weights: {
            taskDivision: -1,
            mentalLoadAwareness: -2,
            fairnessPerception: -1
          }
        }
      ]
    },

    {
      id: 13,
      category: 'responsibilityDistribution',
      type: "scenario",
      text: "When it comes to mental load (planning, organising, remembering):",
      options: [
        {
          text: "We share it equally: both of us keep track of what needs doing",
          weights: {
            taskDivision: 2,
            mentalLoadAwareness: 2,
            fairnessPerception: 2
          }
        },
        {
          text: "One of us carries most of it whilst the other 'helps when asked'",
          weights: {
            taskDivision: -2,
            mentalLoadAwareness: -2,
            fairnessPerception: -2
          }
        },
        {
          text: "We each manage different domains (e.g., one handles finances, one handles social calendar)",
          weights: {
            taskDivision: 1,
            mentalLoadAwareness: 1,
            fairnessPerception: 1
          }
        },
        {
          text: "I don't think my partner realises how much mental load I carry",
          weights: {
            taskDivision: -2,
            mentalLoadAwareness: -2,
            fairnessPerception: -2
          }
        }
      ]
    },

    {
      id: 14,
      category: 'responsibilityDistribution',
      type: "scenario",
      text: "Your perception of fairness in the relationship:",
      options: [
        {
          text: "The division of labour feels genuinely fair to both of us",
          weights: {
            taskDivision: 2,
            mentalLoadAwareness: 2,
            fairnessPerception: 2
          }
        },
        {
          text: "I feel like I do more than my share",
          weights: {
            taskDivision: -2,
            mentalLoadAwareness: -1,
            fairnessPerception: -2
          }
        },
        {
          text: "My partner probably feels like they do more than their share",
          weights: {
            taskDivision: -1,
            mentalLoadAwareness: 0,
            fairnessPerception: -1
          }
        },
        {
          text: "We've never really discussed what 'fair' means to each of us",
          weights: {
            taskDivision: -1,
            mentalLoadAwareness: -2,
            fairnessPerception: -1
          }
        }
      ]
    },

    {
      id: 15,
      category: 'responsibilityDistribution',
      type: "scenario",
      text: "When something needs doing around the house:",
      options: [
        {
          text: "We both notice and take initiative without being asked",
          weights: {
            taskDivision: 2,
            mentalLoadAwareness: 2,
            fairnessPerception: 2
          }
        },
        {
          text: "One of us notices and asks the other to help",
          weights: {
            taskDivision: -1,
            mentalLoadAwareness: -1,
            fairnessPerception: -1
          }
        },
        {
          text: "One of us just does it because asking feels harder than doing",
          weights: {
            taskDivision: -2,
            mentalLoadAwareness: -2,
            fairnessPerception: -2
          }
        },
        {
          text: "We have clear systems and both follow through",
          weights: {
            taskDivision: 2,
            mentalLoadAwareness: 2,
            fairnessPerception: 2
          }
        }
      ]
    },

    // GROWTH & CHANGE PATTERNS (Questions 16-19)
    {
      id: 16,
      category: 'growthChange',
      type: "scenario",
      text: "When you want to pursue personal growth (new career, education, hobby):",
      options: [
        {
          text: "My partner actively supports and encourages me",
          weights: {
            developmentSupport: 2,
            changeAdaptability: 2,
            evolutionCompatibility: 2
          }
        },
        {
          text: "They're neutral: not opposed but not particularly supportive",
          weights: {
            developmentSupport: 0,
            changeAdaptability: 0,
            evolutionCompatibility: 0
          }
        },
        {
          text: "They resist or feel threatened by my growth",
          weights: {
            developmentSupport: -2,
            changeAdaptability: -2,
            evolutionCompatibility: -2
          }
        },
        {
          text: "I don't pursue growth because of how it might affect the relationship",
          weights: {
            developmentSupport: -2,
            changeAdaptability: -1,
            evolutionCompatibility: -2
          }
        }
      ]
    },

    {
      id: 17,
      category: 'growthChange',
      type: "scenario",
      text: "When one of you changes significantly (new interests, values, perspectives):",
      options: [
        {
          text: "We embrace it; change keeps the relationship dynamic and interesting",
          weights: {
            developmentSupport: 2,
            changeAdaptability: 2,
            evolutionCompatibility: 2
          }
        },
        {
          text: "It creates tension. Change feels threatening to our connection",
          weights: {
            developmentSupport: -2,
            changeAdaptability: -2,
            evolutionCompatibility: -2
          }
        },
        {
          text: "We adapt but it takes time and conscious effort",
          weights: {
            developmentSupport: 1,
            changeAdaptability: 1,
            evolutionCompatibility: 1
          }
        },
        {
          text: "We're growing in different directions and it worries me",
          weights: {
            developmentSupport: -1,
            changeAdaptability: 0,
            evolutionCompatibility: -2
          }
        }
      ]
    },

    {
      id: 18,
      category: 'growthChange',
      type: "scenario",
      text: "Your relationship's adaptability to life changes:",
      options: [
        {
          text: "We navigate change well together; it brings us closer",
          weights: {
            developmentSupport: 2,
            changeAdaptability: 2,
            evolutionCompatibility: 2
          }
        },
        {
          text: "Change is hard but we eventually adjust",
          weights: {
            developmentSupport: 0,
            changeAdaptability: 1,
            evolutionCompatibility: 0
          }
        },
        {
          text: "Change tends to destabilise us. We struggle to adapt together",
          weights: {
            developmentSupport: -2,
            changeAdaptability: -2,
            evolutionCompatibility: -1
          }
        },
        {
          text: "We resist change to protect the relationship's stability",
          weights: {
            developmentSupport: -1,
            changeAdaptability: -2,
            evolutionCompatibility: -2
          }
        }
      ]
    },

    {
      id: 19,
      category: 'growthChange',
      type: "scenario",
      text: "Over time, you and your partner:",
      options: [
        {
          text: "Are growing together, evolving in complementary ways",
          weights: {
            developmentSupport: 2,
            changeAdaptability: 2,
            evolutionCompatibility: 2
          }
        },
        {
          text: "Are staying relatively the same, neither growing much",
          weights: {
            developmentSupport: 0,
            changeAdaptability: -1,
            evolutionCompatibility: 0
          }
        },
        {
          text: "Are growing apart, becoming less compatible over time",
          weights: {
            developmentSupport: -2,
            changeAdaptability: 0,
            evolutionCompatibility: -2
          }
        },
        {
          text: "One is growing whilst the other stays the same",
          weights: {
            developmentSupport: -1,
            changeAdaptability: 0,
            evolutionCompatibility: -2
          }
        }
      ]
    },

    // TRUST & TRANSPARENCY (Questions 20-23)
    {
      id: 20,
      category: 'trustTransparency',
      type: "scenario",
      text: "The level of openness in your relationship:",
      options: [
        {
          text: "We share everything; full transparency is our norm",
          weights: {
            opennessLevel: 2,
            privacyBoundaries: -1,
            honestyPractices: 2
          }
        },
        {
          text: "We're mostly open but maintain some privacy in certain areas",
          weights: {
            opennessLevel: 1,
            privacyBoundaries: 1,
            honestyPractices: 1
          }
        },
        {
          text: "There are significant things I don't share with my partner",
          weights: {
            opennessLevel: -2,
            privacyBoundaries: 2,
            honestyPractices: -1
          }
        },
        {
          text: "We're not very open. We keep a lot to ourselves",
          weights: {
            opennessLevel: -2,
            privacyBoundaries: 2,
            honestyPractices: -2
          }
        }
      ]
    },

    {
      id: 21,
      category: 'trustTransparency',
      type: "scenario",
      text: "When it comes to phones, emails, and personal messages:",
      options: [
        {
          text: "We have full access to each other's devices; nothing is off-limits",
          weights: {
            opennessLevel: 2,
            privacyBoundaries: -2,
            honestyPractices: 1
          }
        },
        {
          text: "We respect each other's privacy whilst maintaining trust",
          weights: {
            opennessLevel: 1,
            privacyBoundaries: 2,
            honestyPractices: 2
          }
        },
        {
          text: "One of us is more protective of privacy than the other",
          weights: {
            opennessLevel: 0,
            privacyBoundaries: 1,
            honestyPractices: 0
          }
        },
        {
          text: "There's secrecy that feels uncomfortable or questionable",
          weights: {
            opennessLevel: -2,
            privacyBoundaries: 2,
            honestyPractices: -2
          }
        }
      ]
    },

    {
      id: 22,
      category: 'trustTransparency',
      type: "scenario",
      text: "Honesty in your relationship:",
      options: [
        {
          text: "We're completely honest with each other, even when it's hard",
          weights: {
            opennessLevel: 2,
            privacyBoundaries: 0,
            honestyPractices: 2
          }
        },
        {
          text: "We tell 'white lies' to avoid conflict or hurt feelings",
          weights: {
            opennessLevel: -1,
            privacyBoundaries: 0,
            honestyPractices: -1
          }
        },
        {
          text: "One or both of us sometimes omit things rather than lie directly",
          weights: {
            opennessLevel: -2,
            privacyBoundaries: 1,
            honestyPractices: -2
          }
        },
        {
          text: "Honesty exists but sometimes truth gets stretched or avoided",
          weights: {
            opennessLevel: -1,
            privacyBoundaries: 0,
            honestyPractices: -1
          }
        }
      ]
    },

    {
      id: 23,
      category: 'trustTransparency',
      type: "scenario",
      text: "If you discovered your partner had kept something significant from you:",
      options: [
        {
          text: "I'd be surprised. We don't keep secrets",
          weights: {
            opennessLevel: 2,
            privacyBoundaries: 0,
            honestyPractices: 2
          }
        },
        {
          text: "I'd want to understand why before reacting",
          weights: {
            opennessLevel: 1,
            privacyBoundaries: 1,
            honestyPractices: 1
          }
        },
        {
          text: "I'd feel betrayed; openness is fundamental to trust for me",
          weights: {
            opennessLevel: 2,
            privacyBoundaries: -1,
            honestyPractices: 2
          }
        },
        {
          text: "I wouldn't be too surprised. We each have our private areas",
          weights: {
            opennessLevel: -1,
            privacyBoundaries: 2,
            honestyPractices: 0
          }
        }
      ]
    }
  ],

  // Calculate dimension scores from weighted responses
  calculateScores: (responses) => {
    const dimensionScores = {};
    const dimensionCounts = {};

    // Initialize all dimensions across all categories
    Object.values(RelationshipPatternsProfile.patternCategories).forEach(category => {
      category.dimensions.forEach(dim => {
        dimensionScores[dim] = 0;
        dimensionCounts[dim] = 0;
      });
    });

    // Aggregate weighted scores
    responses.forEach(response => {
      const question = RelationshipPatternsProfile.questions[response.questionId];
      const option = question.options[response.selectedIndex];

      if (option.weights) {
        Object.entries(option.weights).forEach(([dimension, weight]) => {
          if (dimensionScores[dimension] !== undefined) {
            dimensionScores[dimension] += weight;
            dimensionCounts[dimension]++;
          }
        });
      }
    });

    // Normalise to 0-100 scale
    const normalisedScores = {};
    Object.keys(dimensionScores).forEach(dim => {
      if (dimensionCounts[dim] > 0) {
        const avgScore = dimensionScores[dim] / dimensionCounts[dim];
        // Convert from -2 to +2 range to 0-100
        normalisedScores[dim] = Math.round(((avgScore + 2) / 4) * 100);
      } else {
        normalisedScores[dim] = 50;
      }
    });

    return normalisedScores;
  },

  // Determine relationship pattern type
  getPatternType: (scores) => {
    const independenceBalance = scores.independenceBalance || 50;
    const powerDistribution = scores.powerDistribution || 50;
    const responsivenessToDistress = scores.responsivenessToDistress || 50;
    const fairnessPerception = scores.fairnessPerception || 50;

    // Healthy interdependence: balanced independence + balanced power + good emotional support
    if (independenceBalance >= 60 && powerDistribution >= 60 && responsivenessToDistress >= 60) {
      return 'healthyInterdependence';
    }

    // Codependent: low independence + unequal power + high emotional fusion
    if (independenceBalance <= 35 && powerDistribution <= 40) {
      return 'codependentPattern';
    }

    // Parallel lives: high independence but low connection/support
    if (independenceBalance >= 70 && responsivenessToDistress <= 40) {
      return 'parallelLives';
    }

    // Power imbalance: significantly unequal distribution
    if (powerDistribution <= 35 || fairnessPerception <= 35) {
      return 'powerImbalance';
    }

    // Secure connection: balanced across most areas
    const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length;
    if (avgScore >= 55 && avgScore <= 70) {
      return 'secureConnection';
    }

    // Default: emerging patterns
    return 'emergingPatterns';
  },

  patternTypes: {
    healthyInterdependence: {
      name: "Healthy Interdependence",
      tagline: "You've built a relationship that balances autonomy and connection beautifully",
      description: "Your relationship demonstrates healthy interdependence: you maintain individual identities whilst building deep connection. Power is balanced, emotional support is mutual, responsibilities feel fair, and you're growing together. This is what thriving partnerships look like.",
      strengths: [
        "Strong balance between 'me,' 'you,' and 'us'",
        "Mutual emotional support and responsiveness",
        "Equitable power distribution and decision-making",
        "Both partners support each other's growth"
      ],
      growthOpportunities: [
        "Maintain these patterns; they're working",
        "Stay aware as life circumstances change",
        "Model healthy interdependence for others",
        "Continue nurturing individual and shared growth"
      ]
    },

    codependentPattern: {
      name: "Codependent Pattern",
      tagline: "Your identities have become overly fused. Individual 'you' needs strengthening",
      description: "Your relationship shows signs of codependency: blurred boundaries, loss of individual identity, difficulty with separateness, and possible power imbalances. This pattern often stems from anxiety about abandonment. Growth involves reclaiming individual identity whilst maintaining connection.",
      strengths: [
        "Deep commitment to the relationship",
        "Strong desire for closeness and connection",
        "Awareness that change is needed",
        "Capacity for intimacy and attachment"
      ],
      growthOpportunities: [
        "Rebuild individual identity and separate interests",
        "Work on tolerating separateness without anxiety",
        "Explore why fusion feels safer than autonomy",
        "Consider individual therapy or couples counselling"
      ]
    },

    parallelLives: {
      name: "Parallel Lives",
      tagline: "You're independent but disconnected. Connection needs intentional rebuilding",
      description: "You maintain strong individual identities but may have lost emotional connection. You live parallel lives more than an intertwined one. Independence is healthy, but relationships also need interdependence, emotional responsiveness, and shared experiences. The challenge is reconnecting without losing yourself.",
      strengths: [
        "Strong individual identities maintained",
        "Healthy autonomy and independence",
        "Mutual respect for personal space",
        "Low conflict due to separateness"
      ],
      growthOpportunities: [
        "Intentionally create more shared experiences",
        "Increase emotional vulnerability and sharing",
        "Practise interdependence, not just independence",
        "Rebuild emotional responsiveness to each other"
      ]
    },

    powerImbalance: {
      name: "Power Imbalance",
      tagline: "Decision-making and influence feel unequal. Equity needs attention",
      description: "Your relationship shows significant power imbalance: one partner has more influence in decisions, responsibilities feel unfair, or one voice matters more than the other. This pattern erodes connection and breeds resentment. Healthy relationships require equitable power distribution.",
      strengths: [
        "Awareness that the imbalance exists",
        "Stability (even if unequal)",
        "Possibility of renegotiation",
        "One partner may be highly capable"
      ],
      growthOpportunities: [
        "Explicitly discuss power dynamics and fairness",
        "Equalise decision-making processes",
        "Redistribute responsibilities more equitably",
        "Address underlying beliefs about roles and worth"
      ]
    },

    secureConnection: {
      name: "Secure Connection",
      tagline: "You're building healthy patterns with room for continued growth",
      description: "Your relationship shows secure connection across most dimensions. You're not perfect, but you're functional and improving. You balance autonomy and togetherness reasonably well, support each other emotionally, and work toward fairness. Keep building on this solid foundation.",
      strengths: [
        "Balanced patterns in most areas",
        "Mutual emotional support",
        "Reasonable fairness and equity",
        "Commitment to growth and improvement"
      ],
      growthOpportunities: [
        "Identify 1-2 specific patterns to strengthen",
        "Maintain awareness as life changes",
        "Continue open dialogue about needs",
        "Celebrate what's working well"
      ]
    },

    emergingPatterns: {
      name: "Emerging Patterns",
      tagline: "Your relationship patterns are still forming: intentional shaping needed",
      description: "Your patterns aren't yet solidified into clear types. This is common in newer relationships or relationships undergoing significant change. This is actually a good moment: patterns are easier to shape now than after years of entrenchment. Be intentional about the dynamics you're building.",
      strengths: [
        "Flexibility to shape healthier patterns",
        "Not locked into problematic dynamics yet",
        "Awareness before patterns become rigid",
        "Opportunity to build intentionally"
      ],
      growthOpportunities: [
        "Discuss explicitly what patterns you want to build",
        "Practise healthy dynamics consistently",
        "Address imbalances before they become entrenched",
        "Use the modules to guide intentional pattern-building"
      ]
    }
  },

  completionMessage: {
    heading: "Your Relationship Pattern Profile is Ready",
    content: `We've identified the recurring patterns that shape your relationship across six key areas.

These patterns aren't permanent. They're habits developed over time for reasons that probably made sense when they formed. Some serve you well. Others don't.

The good news is that awareness is the first step toward changing patterns that no longer work. And you can intentionally strengthen the patterns that do work.

Let's see what your relationship dynamics reveal...`
  }
};

export default RelationshipPatternsProfile;
